import { auth } from '../firebase/config';

export type AIProvider = 'gemini' | 'openai';

export interface AIInputImage {
  dataUrl?: string;
  base64?: string;
  mimeType?: string;
}

export interface GenerateTextOptions {
  provider?: AIProvider;
  model?: string;
  systemInstruction?: string;
  temperature?: number;
  maxTokens?: number;
  useGoogleSearch?: boolean;
  structuredOutputSchema?: Record<string, unknown> | string;
  structuredOutputName?: string;
  inputImages?: AIInputImage[];
}

export interface GenerateTextResponse {
  text: string;
  provider: AIProvider;
  model: string;
  structuredOutput?: unknown;
  phases?: number;
  usedGoogleSearch?: boolean;
  usage?: {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
  };
  defaultProvider?: AIProvider;
  defaultModel?: string;
}

export interface GenerateImageOptions {
  appId: string;
  assetCategory?: string;
  provider?: AIProvider;
  model?: string;
  aspectRatio?: '1:1' | '3:4' | '4:3' | '9:16' | '16:9';
  size?: '1024x1024' | '1024x1536' | '1536x1024' | 'auto';
  n?: number;
}

export interface ImageAssetRecord {
  assetId: string;
  appId: string;
  type: 'image';
  category: string;
  provider: AIProvider;
  model: string;
  downloadUrl: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface GenerateImageResponse {
  provider: AIProvider;
  model: string;
  images: ImageAssetRecord[];
  taskType?: 'image';
  defaultProvider?: AIProvider;
  defaultModel?: string;
}

export interface GenerateVideoOptions {
  appId: string;
  assetCategory?: string;
  provider?: AIProvider;
  model?: string;
  aspectRatio?: '16:9' | '9:16';
  size?: '1280x720' | '720x1280' | '1024x1792' | '1792x1024';
  seconds?: number;
  timeoutMs?: number;
}

export interface VideoAssetRecord {
  assetId: string;
  appId: string;
  type: 'video';
  category: string;
  provider: AIProvider;
  model: string;
  downloadUrl: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface GenerateVideoResponse {
  provider: AIProvider;
  model: string;
  videos: VideoAssetRecord[];
  taskType?: 'video';
  defaultProvider?: AIProvider;
  defaultModel?: string;
}

export interface DeleteAssetResponse {
  ok: boolean;
  storageFileDeleted: boolean;
  appAssetsDeleted: number;
  marketingAssetsDeleted: number;
  storagePath?: string | null;
  assetId?: string | null;
}

export interface MarketingAssetsZipResponse {
  ok: boolean;
  appId: string;
  totalAssets: number;
  storagePath: string;
  downloadUrl: string;
  fileName: string;
}

export interface StoreScreenshotsZipResponse {
  ok: boolean;
  appId: string;
  versionId: string;
  totalAssets: number;
  storagePath: string;
  downloadUrl: string;
  fileName: string;
  zipCreatedAt: string;
}

export interface RepurposeTargetSpec {
  platform: string;
  formatKey: string;
  width: number;
  height: number;
  crop?: {
    focalX?: number;
    focalY?: number;
    zoom?: number;
  };
}

export interface RepurposeMarketingAssetsResponse {
  ok: boolean;
  appId: string;
  sourceStoragePath: string;
  fileName: string;
  storagePath: string;
  downloadUrl: string;
  created: Array<{
    platform: string;
    formatKey: string;
    width: number;
    height: number;
    fileName: string;
  }>;
}

export interface GenerateIconVariantsOptions {
  appId: string;
  sourceStoragePath: string;
  platform: 'ios' | 'android' | 'both';
  crop: {
    offsetX: number;
    offsetY: number;
    zoom: number;
  };
}

export interface GenerateIconVariantsResponse {
  variants: Array<{
    platform: 'ios' | 'android' | 'capacitor';
    size: number;
    name: string;
    fileName: string;
    base64: string;
    mimeType: string;
  }>;
  taskType?: 'iconVariants';
}

export interface GenerateFeatureGraphicOptions {
  appId: string;
  sourceStoragePath: string;
  crop: {
    offsetX: number;
    offsetY: number;
    zoom: number;
  };
}

export interface GenerateFeatureGraphicResponse {
  width: number;
  height: number;
  fileName: string;
  base64: string;
  mimeType: string;
  taskType?: 'featureGraphic';
}

export interface GenerateSplashVariantsOptions {
  appId: string;
  sourceStoragePath: string;
  darkSourceStoragePath: string;
  crop: {
    offsetX: number;
    offsetY: number;
    zoom: number;
  };
}

export interface GenerateSplashVariantsResponse {
  variants: Array<{
    platform: 'capacitor';
    name: string;
    fileName: string;
    width: number;
    height: number;
    base64: string;
    mimeType: string;
  }>;
  taskType?: 'splashVariants';
}

export interface StoreMetadataResponse {
  platform: 'android' | 'ios' | 'unknown';
  found: boolean;
  source?: string;
  query: string;
  storeUrl?: string | null;
  appId?: string | null;
  title?: string | null;
  description?: string | null;
  developer?: string | null;
  category?: string | null;
  rating?: string | number | null;
  ratingCount?: string | number | null;
  installs?: string | null;
  releaseDate?: string | null;
  updatedDate?: string | null;
  icon?: string | null;
  citation?: {
    title: string;
    url: string;
    publisher?: string;
    publishedAt?: string;
    note?: string;
  };
  reason?: string;
  taskType?: 'storeMetadata';
}

class AIService {
  private static readonly DEFAULT_ENDPOINT =
    'https://europe-west1-translatorx-5774e.cloudfunctions.net/generateAIContent';

  static readonly DEFAULT_PROVIDER: AIProvider = 'gemini';
  static readonly DEFAULT_IMAGE_PROVIDER: AIProvider = 'openai';

  static readonly DEFAULT_TEXT_MODELS: Record<AIProvider, string> = {
    gemini: 'gemini-2.5-flash-lite',
    openai: 'gpt-4.1-mini',
  };

  // "Nano Banana" image model on Gemini
  static readonly DEFAULT_IMAGE_MODELS: Record<AIProvider, string> = {
    gemini: 'gemini-2.5-flash-image',
    openai: 'gpt-image-2.5-flare',
  };

  static readonly DEFAULT_VIDEO_MODELS: Record<AIProvider, string> = {
    gemini: 'veo-3.1-generate-preview',
    openai: 'sora-2',
  };

  static getDefaultModel(provider: AIProvider = AIService.DEFAULT_PROVIDER): string {
    return AIService.DEFAULT_TEXT_MODELS[provider];
  }

  static getDefaultImageModel(provider: AIProvider = AIService.DEFAULT_IMAGE_PROVIDER): string {
    return AIService.DEFAULT_IMAGE_MODELS[provider];
  }

  static getDefaultVideoModel(provider: AIProvider = AIService.DEFAULT_PROVIDER): string {
    return AIService.DEFAULT_VIDEO_MODELS[provider];
  }

  static async generateText(
    prompt: string,
    options: GenerateTextOptions = {}
  ): Promise<GenerateTextResponse> {
    const trimmedPrompt = prompt?.trim();
    if (!trimmedPrompt) {
      throw new Error('Prompt is required');
    }

    const normalizedRequestedModel = AIService.normalizeTextModelAlias(options.model);
    const inferredProvider = normalizedRequestedModel
      ? AIService.inferProviderFromModel(normalizedRequestedModel)
      : null;
    const provider = options.provider ?? inferredProvider ?? AIService.DEFAULT_PROVIDER;
    const model = normalizedRequestedModel ?? AIService.getDefaultModel(provider);

    const response = await AIService.callGenerateAIContent({
      prompt: trimmedPrompt,
      provider,
      model,
      systemInstruction: options.systemInstruction,
      temperature: options.temperature,
      maxTokens: options.maxTokens,
      useGoogleSearch: options.useGoogleSearch,
      structuredOutputSchema: options.structuredOutputSchema,
      structuredOutputName: options.structuredOutputName,
      inputImages: options.inputImages,
    });

    return response as GenerateTextResponse;
  }

  static async generateImage(
    prompt: string,
    options: GenerateImageOptions
  ): Promise<GenerateImageResponse> {
    const trimmedPrompt = prompt?.trim();
    if (!trimmedPrompt) {
      throw new Error('Prompt is required');
    }
    if (!options.appId?.trim()) {
      throw new Error('appId is required to store image assets');
    }

    const inferredProvider = options.model
      ? AIService.inferProviderFromModel(options.model)
      : null;
    const provider = options.provider ?? inferredProvider ?? AIService.DEFAULT_IMAGE_PROVIDER;
    const model = options.model ?? AIService.getDefaultImageModel(provider);

    const response = await AIService.callGenerateAIContent({
      taskType: 'image',
      prompt: trimmedPrompt,
      appId: options.appId,
      assetCategory: options.assetCategory ?? 'general',
      provider,
      model,
      aspectRatio: options.aspectRatio,
      size: options.size,
      n: options.n,
    });

    return response as GenerateImageResponse;
  }

  static async generateJSON<T = unknown>(
    prompt: string,
    options: GenerateTextOptions = {}
  ): Promise<T> {
    const response = await AIService.generateText(prompt, options);

    if (response.structuredOutput !== undefined && response.structuredOutput !== null) {
      return response.structuredOutput as T;
    }

    const parsed = AIService.extractJsonFromText(response.text);
    if (parsed !== null) {
      return parsed as T;
    }
    throw new Error('AI response was not valid JSON');
  }

  static async generateVideo(
    prompt: string,
    options: GenerateVideoOptions
  ): Promise<GenerateVideoResponse> {
    const trimmedPrompt = prompt?.trim();
    if (!trimmedPrompt) {
      throw new Error('Prompt is required');
    }
    if (!options.appId?.trim()) {
      throw new Error('appId is required to store video assets');
    }

    const provider = options.provider ?? AIService.DEFAULT_PROVIDER;
    const model = options.model ?? AIService.getDefaultVideoModel(provider);

    const response = await AIService.callGenerateAIContent({
      taskType: 'video',
      prompt: trimmedPrompt,
      appId: options.appId,
      assetCategory: options.assetCategory ?? 'general',
      provider,
      model,
      aspectRatio: options.aspectRatio,
      size: options.size,
      seconds: options.seconds,
      timeoutMs: options.timeoutMs,
    });

    return response as GenerateVideoResponse;
  }

  static async generateIconVariants(
    options: GenerateIconVariantsOptions
  ): Promise<GenerateIconVariantsResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for icon variant generation');
    }
    if (!options.sourceStoragePath?.trim()) {
      throw new Error('sourceStoragePath is required for icon variant generation');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'iconVariants',
      appId: options.appId,
      sourceStoragePath: options.sourceStoragePath,
      platform: options.platform,
      crop: options.crop,
    });

    return response as GenerateIconVariantsResponse;
  }

  static async generateFeatureGraphic(
    options: GenerateFeatureGraphicOptions
  ): Promise<GenerateFeatureGraphicResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for feature graphic generation');
    }
    if (!options.sourceStoragePath?.trim()) {
      throw new Error('sourceStoragePath is required for feature graphic generation');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'featureGraphic',
      appId: options.appId,
      sourceStoragePath: options.sourceStoragePath,
      crop: options.crop,
    });

    return response as GenerateFeatureGraphicResponse;
  }

  static async generateSplashVariants(
    options: GenerateSplashVariantsOptions
  ): Promise<GenerateSplashVariantsResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for splash variant generation');
    }
    if (!options.sourceStoragePath?.trim()) {
      throw new Error('sourceStoragePath is required for splash variant generation');
    }
    if (!options.darkSourceStoragePath?.trim()) {
      throw new Error('darkSourceStoragePath is required for splash variant generation');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'splashVariants',
      appId: options.appId,
      sourceStoragePath: options.sourceStoragePath,
      darkSourceStoragePath: options.darkSourceStoragePath,
      crop: options.crop,
    });

    return response as GenerateSplashVariantsResponse;
  }

  static async exportAppleCreativeAsset(options: {
    appId: string;
    placement: import('../utils/appleCreative').ApplePlacement;
    width: number;
    height: number;
    sourceStoragePath?: string;
    sourceDataUrl?: string;
    crop: import('../utils/appleCreative').AppleCrop;
  }): Promise<import('./AppleCreativeService').AppleLibraryAsset & { fileName: string }> {
    if (!options.appId || (!options.sourceStoragePath && !options.sourceDataUrl)) {
      throw new Error('Select an app and a source image.');
    }
    return await AIService.callGenerateAIContent({ taskType: 'appleCreativeAsset', ...options }) as
      import('./AppleCreativeService').AppleLibraryAsset & { fileName: string };
  }

  static async getStoreMetadata(appQuery: string): Promise<StoreMetadataResponse> {
    const query = appQuery?.trim();
    if (!query) {
      throw new Error('appQuery is required for store metadata lookup');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'storeMetadata',
      appQuery: query,
    });

    return response as StoreMetadataResponse;
  }

  static async deleteGeneratedAsset(options: {
    appId: string;
    storagePath?: string;
    assetId?: string;
    marketingAssetRecordId?: string;
  }): Promise<DeleteAssetResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for asset deletion');
    }
    if (
      !options.storagePath?.trim() &&
      !options.assetId?.trim() &&
      !options.marketingAssetRecordId?.trim()
    ) {
      throw new Error(
        'At least one of storagePath, assetId, marketingAssetRecordId is required for deletion'
      );
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'deleteAsset',
      appId: options.appId,
      storagePath: options.storagePath,
      assetId: options.assetId,
      marketingAssetRecordId: options.marketingAssetRecordId,
    });

    return response as DeleteAssetResponse;
  }

  static async createMarketingAssetsZip(options: {
    appId: string;
    reportId?: string;
  }): Promise<MarketingAssetsZipResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for marketing assets zip export');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'marketingAssetsZip',
      appId: options.appId,
      reportId: options.reportId,
    });

    return response as MarketingAssetsZipResponse;
  }

  static async createStoreScreenshotsZip(options: {
    appId: string;
    versionId: string;
  }): Promise<StoreScreenshotsZipResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for screenshot zip export');
    }
    if (!options.versionId?.trim()) {
      throw new Error('versionId is required for screenshot zip export');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'storeScreenshotsZip',
      appId: options.appId,
      versionId: options.versionId,
    });

    return response as StoreScreenshotsZipResponse;
  }

  static async repurposeMarketingAssets(options: {
    appId: string;
    sourceStoragePath: string;
    targets: RepurposeTargetSpec[];
    crop?: {
      focalX?: number;
      focalY?: number;
      zoom?: number;
    };
  }): Promise<RepurposeMarketingAssetsResponse> {
    if (!options.appId?.trim()) {
      throw new Error('appId is required for asset repurposing');
    }
    if (!options.sourceStoragePath?.trim()) {
      throw new Error('sourceStoragePath is required for asset repurposing');
    }
    if (!Array.isArray(options.targets) || options.targets.length === 0) {
      throw new Error('At least one target is required for asset repurposing');
    }

    const response = await AIService.callGenerateAIContent({
      taskType: 'repurposeMarketingAssets',
      appId: options.appId,
      sourceStoragePath: options.sourceStoragePath,
      targets: options.targets,
      crop: options.crop,
    });

    return response as RepurposeMarketingAssetsResponse;
  }

  private static inferProviderFromModel(model: string): AIProvider | null {
    const normalized = model.toLowerCase();
    if (normalized.includes('gemini')) {
      return 'gemini';
    }
    if (normalized.includes('veo')) {
      return 'gemini';
    }
    if (normalized.includes('sora')) {
      return 'openai';
    }
    if (
      normalized.startsWith('gpt') ||
      normalized.startsWith('o1') ||
      normalized.startsWith('o3') ||
      normalized.startsWith('o4')
    ) {
      return 'openai';
    }
    return null;
  }

  private static normalizeTextModelAlias(model?: string): string | undefined {
    const trimmed = String(model || "").trim();
    if (!trimmed) return undefined;
    const aliasMap: Record<string, string> = {
      "gemini-3-flash-lite": "gemini-2.5-flash-lite",
      "gemini-3-flash": "gemini-2.5-flash",
      "gemini-3-pro": "gemini-2.5-pro",
    };
    return aliasMap[trimmed] || trimmed;
  }

  private static extractJsonFromText(text: string): unknown | null {
    const raw = String(text || "").trim();
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      // continue
    }

    const fenced =
      raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced);
      } catch {
        // continue
      }
    }

    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(raw.slice(start, end + 1));
      } catch {
        return null;
      }
    }

    return null;
  }

  private static async callGenerateAIContent(payload: Record<string, unknown>): Promise<unknown> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Authentication required. Please sign in.');
    }

    const idToken = await currentUser.getIdToken();
    const endpoint =
      process.env.REACT_APP_GENERATE_AI_CONTENT_URL || AIService.DEFAULT_ENDPOINT;
    const requestBody = JSON.stringify(payload);
    const payloadBytes = new Blob([requestBody]).size;
    const maxRecommendedBytes = 4_500_000;
    if (payloadBytes > maxRecommendedBytes) {
      throw new Error(
        `Request payload too large (${Math.round(
          payloadBytes / 1024,
        )}KB). Please reduce or compress screenshot inputs.`,
      );
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: requestBody,
    });

    const rawText = await response.text();
    let responseData: any = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        responseData?.error ||
        responseData?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      throw new Error(
        detailedMessage || `Request failed with status ${response.status}`
      );
    }

    return responseData;
  }
}

export default AIService;
